import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { GuardiansFilterValues } from "./GuardiansFilterBar";
import {
  GuardiansMoreFiltersPopover,
  popoverCanReset,
} from "./GuardiansMoreFiltersPopover";
import type { GuardiansFilterOptions } from "./useGuardiansFilterOptions";
import {
  guardiansToolbarFiltersButtonClass,
  GUARDIANS_TOOLBAR_ACTION_MIN_WIDTH_PX,
} from "./guardiansToolbarButton";
import { CompressibleToolbarLabel } from "./CompressibleToolbarLabel";
import { GuardiansToolbarAnimatedSlot } from "./GuardiansToolbarAnimatedSlot";
import type { AuditOriginFilter, GuardiansView } from "../guardiansParams";
import type { AuditBucket } from "../audits/useAudits";
import type { ScreeningBucket } from "../screenings/useScreenings";
import {
  guardiansActiveFilterCount,
  guardiansPopoverResetTabPatch,
  guardiansPopoverResetValues,
  type GuardiansTabFilterState,
} from "./guardiansViewFilters";

export interface GuardiansFiltersButtonProps {
  visible: boolean;
  view: GuardiansView;
  values: GuardiansFilterValues;
  tabFilters: GuardiansTabFilterState;
  options: GuardiansFilterOptions;
  onChange: (next: GuardiansFilterValues) => void;
  onAuditBucketChange: (bucket: AuditBucket) => void;
  onAuditOriginChange: (origin: AuditOriginFilter) => void;
  onScreeningBucketChange: (bucket: ScreeningBucket) => void;
  onTabFilterPatch: (patch: ReturnType<typeof guardiansPopoverResetTabPatch>) => void;
}

export function GuardiansFiltersButton({
  visible,
  view,
  values,
  tabFilters,
  options,
  onChange,
  onAuditBucketChange,
  onAuditOriginChange,
  onScreeningBucketChange,
  onTabFilterPatch,
}: GuardiansFiltersButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const activeCount = guardiansActiveFilterCount(view, values, tabFilters);

  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);

  const handleReset = () => {
    onChange(guardiansPopoverResetValues(view, values));
    onTabFilterPatch(guardiansPopoverResetTabPatch(view));
    setOpen(false);
  };

  return (
    <GuardiansToolbarAnimatedSlot visible={visible} asWorkbenchSlot={false}>
      <div className="relative min-w-0 shrink" data-toolbar-action data-testid="guardians-filters-slot">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={guardiansToolbarFiltersButtonClass}
          style={{ minWidth: GUARDIANS_TOOLBAR_ACTION_MIN_WIDTH_PX }}
          title="Filters"
          data-testid="guardians-more-filters"
          data-visible={visible ? "true" : "false"}
        >
          {activeCount > 0 ? (
            <span
              className="inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full bg-indigo-500 px-1 text-[10px] font-semibold text-white"
              data-testid="guardians-filters-badge"
            >
              {activeCount}
            </span>
          ) : (
            <FontAwesomeIcon icon={faFilter} className="shrink-0 text-slate-400" />
          )}
          <CompressibleToolbarLabel label="Filters" className="min-w-0" />
        </button>
        {open ? (
          <div className="absolute right-0 top-full z-50 mt-2">
            <GuardiansMoreFiltersPopover
              view={view}
              values={values}
              tabFilters={tabFilters}
              missionTemplates={options.missionTemplates}
              agents={options.agents}
              catalogues={options.catalogues}
              onChange={onChange}
              onAuditBucketChange={onAuditBucketChange}
              onAuditOriginChange={onAuditOriginChange}
              onScreeningBucketChange={onScreeningBucketChange}
              onReset={handleReset}
              canReset={popoverCanReset(view, values, tabFilters)}
              onClose={() => setOpen(false)}
              anchorRef={buttonRef}
            />
          </div>
        ) : null}
      </div>
    </GuardiansToolbarAnimatedSlot>
  );
}
